import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useLocalization } from "@/context/localization-context";
import { errorMessage, savePodOverride } from "../../api";
import { Ago, FetchingDot, SectionCard } from "../../components/primitives";
import type { PodOverride, PodsSection } from "../../types";

/**
 * Manual GPU pods (backend vision.pod_override): pods added here join the autoscaler's, and with "ignore the
 * autoscaler" on they are the whole pool - the autoscaler's snapshots are still stored, only not served.
 * Every change is saved at once and the backend's pool follows it.
 */
export function ManualPods({ section }: { section: PodsSection }) {
  const { t } = useLocalization();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const override = section.override;
  if (!override) {
    return null;
  }

  const save = async (next: Pick<PodOverride, "ignore_autoscaler" | "pods">) => {
    setSaving(true);
    setError(null);
    try {
      await savePodOverride(next);
      await queryClient.invalidateQueries({ queryKey: ["monitoring", "live"] });
      return true;
    } catch (e) {
      setError(errorMessage(e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    const pod = draft.trim();
    if (pod && (await save({ ignore_autoscaler: override.ignore_autoscaler, pods: [...override.pods, pod] }))) {
      setDraft("");
    }
  };

  return (
    <SectionCard
      title={t("monitoring.pods.manual.title")}
      description={t("monitoring.pods.manual.description")}
      actions={<FetchingDot active={saving} />}
    >
      <div className="flex flex-col gap-4">
        <label className="flex items-start gap-3">
          <Switch
            checked={override.ignore_autoscaler}
            disabled={saving}
            onCheckedChange={(checked) => void save({ ignore_autoscaler: checked, pods: override.pods })}
            className="mt-0.5"
          />
          <span className="flex flex-col">
            <span className="text-sm font-medium">{t("monitoring.pods.manual.ignore")}</span>
            <span className="text-xs text-muted-foreground">{t("monitoring.pods.manual.ignoreHint")}</span>
          </span>
        </label>
        {override.ignore_autoscaler && override.pods.length === 0 && (
          <div className="text-xs text-amber-400">{t("monitoring.pods.manual.emptyWarning")}</div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {override.pods.length === 0 ? (
            <span className="text-xs text-muted-foreground">{t("monitoring.pods.manual.none")}</span>
          ) : (
            override.pods.map((pod) => (
              <span
                key={pod}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 py-1 pl-2 pr-1 font-mono text-xs"
              >
                {pod}
                <button
                  type="button"
                  disabled={saving}
                  aria-label={t("monitoring.pods.manual.remove", { pod })}
                  title={t("monitoring.pods.manual.remove", { pod })}
                  onClick={() =>
                    void save({ ignore_autoscaler: override.ignore_autoscaler, pods: override.pods.filter((p) => p !== pod) })
                  }
                  className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                >
                  <X className="size-3.5" />
                </button>
              </span>
            ))
          )}
        </div>

        <form
          className="flex flex-wrap gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void add();
          }}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="213.173.104.82:27282"
            aria-label={t("monitoring.pods.manual.inputLabel")}
            className="h-8 w-full max-w-xs font-mono text-xs sm:w-64"
            spellCheck={false}
            autoComplete="off"
          />
          <Button type="submit" size="sm" disabled={saving || !draft.trim()}>
            {t("monitoring.pods.manual.add")}
          </Button>
        </form>

        <div className="flex flex-col gap-1 text-xs text-muted-foreground">
          <span>
            {t("monitoring.pods.manual.autoscaler", {
              pods: (section.configured ?? []).join(", ") || t("monitoring.pods.manual.autoscalerNone"),
            })}
          </span>
          {override.updated_at && (
            <span>
              {t("monitoring.pods.manual.changed")} <Ago ts={override.updated_at} />
              {override.updated_by ? ` · ${override.updated_by}` : ""}
            </span>
          )}
        </div>
        {error && <div className="text-xs text-red-400">{error}</div>}
      </div>
    </SectionCard>
  );
}
